import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/ui/tooltip";
import ExportToolbar from "../components/ExportToolbar";
import { 
  FileDown, RefreshCw, Loader2, BarChart3, CheckCircle, AlertTriangle, 
  AlertCircle, Info, ChevronDown, ChevronRight, Target
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoTip = ({ text, side = "top" }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="inline-flex items-center justify-center ml-1 text-zinc-400 hover:text-[#0047AB] transition-colors focus:outline-none">
        <Info size={14} />
      </button>
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-zinc-900 text-white border-none px-3 py-2">
      {text}
    </TooltipContent>
  </Tooltip>
);

const TrafficLight = ({ status, size = "md" }) => {
  const sizes = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-6 h-6" };
  const colors = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    on_track: "bg-emerald-500",
    at_risk: "bg-amber-500",
    critical: "bg-red-500"
  };
  return <span className={`inline-block rounded-full ${sizes[size]} ${colors[status] || 'bg-zinc-300'}`} />;
};

const KPIDashboardView = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedContracts, setExpandedContracts] = useState({});

  useEffect(() => { fetchSummary(); }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/kpi-dashboard/summary`);
      setSummary(res.data);
    } catch (e) {
      toast.error("Failed to load KPI dashboard");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/kpi-dashboard/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kpi_report_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const toggleContract = (id) => setExpandedContracts(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#0047AB]" size={32} />
        <span className="ml-3 text-zinc-500">Loading KPI Dashboard...</span>
      </div>
    );
  }

  if (!summary || summary.total_kpis === 0) {
    return (
      <div className="space-y-6" data-testid="kpi-dashboard-view">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black font-['Chivo'] uppercase tracking-tight">KPI Dashboard</h1>
            <p className="text-zinc-500 mt-1">Aggregated KPI summary across all your projects</p>
          </div>
        </div>
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center">
          <BarChart3 className="mx-auto mb-4 text-zinc-300" size={48} />
          <h3 className="font-bold text-lg mb-2">No KPI Data Available</h3>
          <p className="text-zinc-500 text-sm">Initialize KPIs on your contracts in Project Controls to see aggregated data here.</p>
        </div>
      </div>
    );
  }

  const healthColor = { green: "text-emerald-600 bg-emerald-50 border-emerald-200", amber: "text-amber-600 bg-amber-50 border-amber-200", red: "text-red-600 bg-red-50 border-red-200" };
  const healthLabel = { green: "Healthy", amber: "Attention Needed", red: "Critical Issues" };

  return (
    <div className="space-y-6" data-testid="kpi-dashboard-view" data-export-region>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-['Chivo'] uppercase tracking-tight flex items-center gap-2">
            KPI Dashboard <InfoTip text="Aggregated view of all KPIs across your projects with traffic-light status indicators. Export to PDF for stakeholder reporting." />
          </h1>
          <p className="text-zinc-500 mt-1">{summary.total_contracts} project{summary.total_contracts !== 1 ? 's' : ''} | {summary.total_kpis} KPIs tracked</p>
        </div>
        <div className="flex gap-2">
          <ExportToolbar title="KPI Dashboard" pdfFilename="kpi-dashboard" csvData={summary.kpi_details?.map(k => ({ name: k.name, value: k.value, status: k.status, actual: k.actual, planned: k.planned })) || null} csvFilename="kpi-data" />
          <Button onClick={fetchSummary} variant="outline" className="border-2" data-testid="btn-refresh-kpi-dash">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
          <Button onClick={exportPDF} disabled={exporting} className="bg-[#0047AB] text-white" data-testid="btn-export-kpi-pdf">
            {exporting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Exporting...</> : <><FileDown size={16} className="mr-2" /> Export PDF Report</>}
          </Button>
        </div>
      </div>

      {/* Overall Health Banner */}
      <div className={`border-2 p-6 flex items-center justify-between ${healthColor[summary.overall_health]}`} data-testid="overall-health-banner">
        <div className="flex items-center gap-4">
          <TrafficLight status={summary.overall_health} size="lg" />
          <div>
            <h2 className="font-bold text-lg uppercase">{healthLabel[summary.overall_health]}</h2>
            <p className="text-sm opacity-80">Overall project portfolio health status</p>
          </div>
        </div>
        <div className="flex gap-6">
          <div className="text-center">
            <p className="text-2xl font-black">{summary.total_on_track}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-70">On Track</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-black">{summary.total_at_risk}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-70">At Risk</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-black">{summary.total_critical}</p>
            <p className="text-[10px] uppercase tracking-wider opacity-70">Critical</p>
          </div>
        </div>
      </div>

      {/* Traffic Light Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="card-on-track">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center">
              <CheckCircle size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-700">On Track</p>
              <p className="text-3xl font-black font-['Chivo'] text-emerald-800">{summary.total_on_track}</p>
            </div>
          </div>
          <Progress value={summary.total_kpis > 0 ? (summary.total_on_track / summary.total_kpis) * 100 : 0} className="h-2 bg-emerald-200" />
          <p className="text-xs text-emerald-600 mt-1">{summary.total_kpis > 0 ? ((summary.total_on_track / summary.total_kpis) * 100).toFixed(0) : 0}% of all KPIs</p>
        </div>
        <div className="border-2 border-amber-300 bg-amber-50 p-5" data-testid="card-at-risk">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
              <AlertTriangle size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-amber-700">At Risk</p>
              <p className="text-3xl font-black font-['Chivo'] text-amber-800">{summary.total_at_risk}</p>
            </div>
          </div>
          <Progress value={summary.total_kpis > 0 ? (summary.total_at_risk / summary.total_kpis) * 100 : 0} className="h-2 bg-amber-200" />
          <p className="text-xs text-amber-600 mt-1">{summary.total_kpis > 0 ? ((summary.total_at_risk / summary.total_kpis) * 100).toFixed(0) : 0}% of all KPIs</p>
        </div>
        <div className="border-2 border-red-300 bg-red-50 p-5" data-testid="card-critical">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-500 flex items-center justify-center">
              <AlertCircle size={20} className="text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-red-700">Critical</p>
              <p className="text-3xl font-black font-['Chivo'] text-red-800">{summary.total_critical}</p>
            </div>
          </div>
          <Progress value={summary.total_kpis > 0 ? (summary.total_critical / summary.total_kpis) * 100 : 0} className="h-2 bg-red-200" />
          <p className="text-xs text-red-600 mt-1">{summary.total_kpis > 0 ? ((summary.total_critical / summary.total_kpis) * 100).toFixed(0) : 0}% of all KPIs</p>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(summary.categories).length > 0 && (
        <div className="border border-zinc-200 bg-white" data-testid="category-breakdown">
          <div className="p-4 border-b border-zinc-200 bg-zinc-50">
            <h3 className="font-bold font-['Chivo'] uppercase text-sm flex items-center gap-1">
              KPIs by Category <InfoTip text="Distribution of KPIs across financial, schedule, risk, quality, performance, and contract categories." />
            </h3>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(summary.categories).map(([cat, data]) => (
              <div key={cat} className="p-3 border border-zinc-200 text-center">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{cat}</p>
                <p className="text-xl font-black font-['Chivo']">{data.total}</p>
                <div className="flex justify-center gap-1 mt-2">
                  <span className="text-[10px] text-emerald-600">{data.on_track}G</span>
                  <span className="text-[10px] text-amber-600">{data.at_risk}A</span>
                  <span className="text-[10px] text-red-600">{data.critical}R</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-Contract Details */}
      <div className="space-y-3" data-testid="per-contract-kpis">
        <h3 className="font-bold font-['Chivo'] uppercase text-sm flex items-center gap-1">
          Project-Level KPI Details <InfoTip text="Click on a project to expand and see individual KPI values with traffic-light status." />
        </h3>
        {summary.per_contract.map(pc => (
          <div key={pc.contract_id} className="border border-zinc-200 bg-white" data-testid={`contract-kpi-${pc.contract_id}`}>
            <button onClick={() => toggleContract(pc.contract_id)} className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors">
              <div className="flex items-center gap-3">
                <TrafficLight status={pc.health} size="md" />
                <div className="text-left">
                  <p className="font-bold text-sm">{pc.project_name}</p>
                  <p className="text-xs text-zinc-500">{pc.sector} | {pc.framework} | {pc.total_kpis} KPIs</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex gap-3 text-xs">
                  <span className="text-emerald-600 font-medium">{pc.on_track} OK</span>
                  <span className="text-amber-600 font-medium">{pc.at_risk} Risk</span>
                  <span className="text-red-600 font-medium">{pc.critical} Crit</span>
                </div>
                {expandedContracts[pc.contract_id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </button>
            {expandedContracts[pc.contract_id] && (
              <div className="border-t border-zinc-200 p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 text-left">
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold">KPI Name</th>
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold">Category</th>
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold text-center">Actual</th>
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold text-center">Target</th>
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold text-center">Unit</th>
                        <th className="pb-2 text-xs uppercase text-zinc-500 font-bold text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pc.kpis.map(kpi => (
                        <tr key={kpi.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="py-2 font-medium">{kpi.name}</td>
                          <td className="py-2 text-zinc-500 capitalize">{kpi.category}</td>
                          <td className="py-2 text-center font-mono">{kpi.value}</td>
                          <td className="py-2 text-center font-mono text-zinc-500">{kpi.target}</td>
                          <td className="py-2 text-center text-zinc-400">{kpi.unit}</td>
                          <td className="py-2 text-center">
                            <Badge className={`text-[10px] font-bold ${
                              kpi.status === 'on_track' ? 'bg-emerald-500 text-white border-none' :
                              kpi.status === 'at_risk' ? 'bg-amber-500 text-white border-none' :
                              'bg-red-500 text-white border-none'
                            }`}>
                              {kpi.status === 'on_track' ? 'ON TRACK' : kpi.status === 'at_risk' ? 'AT RISK' : 'CRITICAL'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default KPIDashboardView;
